import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AuthContext from '../../contexts/AuthContext';
import api from '../../api/api';
import '../../styles/CreateUserPage.css';

const CreateUserPage = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get user ID from URL params if editing
  const isEditMode = !!id;    // Check if we're in edit mode
  const { user } = useContext(AuthContext); // Get current user for default center

  const [loading, setLoading] = useState(false);
  const [fetchingUser, setFetchingUser] = useState(isEditMode);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [centers, setCenters] = useState([]);
  const [classes, setClasses] = useState([]);
  const [unlinkedStudents, setUnlinkedStudents] = useState([]);
  const [linkedStudents, setLinkedStudents] = useState([]);
  const [studentParents, setStudentParents] = useState([]);
  const [availableParents, setAvailableParents] = useState([]);
  const [parentChildren, setParentChildren] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: '',
    center: !isEditMode && user?.center ? user.center : '', // Default to admin's center for new users
    password: '',
    confirmPassword: '',
    sendWelcomeEmail: !isEditMode, // Only true for new users
    // Role-specific fields
    specializations: '',  // for teachers
    assignedClasses: [],  // for teachers
    dateOfBirth: '',      // for students (replacing age)
    year: '',            // for students (replacing grade)
    assignedClasses: [], // for students (multi-class support)
    selectedParents: [],  // for students (parent selection during creation)
    linkedChildren: [],  // for parents (unlinked students)
    additionalChildren: [],  // for parents (already linked students to add as second parent)
    // Parent-specific fields
    relationship: '',
    preferredContactMethod: 'email',
    notes: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [resendingEmail, setResendingEmail] = useState(false);
  const [lastEmailSent, setLastEmailSent] = useState(null);
  const [userData, setUserData] = useState(null);

  // Fetch centers and classes
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch centers
        const centersResponse = await api.get('/centers');
        const centersData = centersResponse.data.success
          ? centersResponse.data.data
          : centersResponse.data;
        setCenters(Array.isArray(centersData) ? centersData : []);

        // Fetch classes (halaqas)
        const classesResponse = await api.get('/halaqas');
        const classesData = classesResponse.data.success
          ? classesResponse.data.data
          : classesResponse.data;
        setClasses(Array.isArray(classesData) ? classesData : []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  // Debug log for assigned classes
  useEffect(() => {
    if (formData.assignedClasses.length > 0) {
      console.log('Current formData.assignedClasses:', formData.assignedClasses);
    }
  }, [formData.assignedClasses]);

  // Fetch unlinked students when role is 'parent' and not in edit mode
  useEffect(() => {
    const fetchUnlinkedStudents = async () => {
      if (formData.role === 'parent' && !isEditMode) {
        try {
          const response = await api.get('/students?unlinked=true');
          const students = response.data || [];
          setUnlinkedStudents(students);
        } catch (error) {
          console.error('Error fetching unlinked students:', error);
          setUnlinkedStudents([]);
        }
      } else {
        setUnlinkedStudents([]);
      }
    };

    const fetchLinkedStudents = async () => {
      if (formData.role === 'parent' && !isEditMode) {
        try {
          const response = await api.get('/students'); // Get all students
          console.log('All students response:', response.data);
          // Filter students that already have parents (for adding second parent)
          const studentsWithParents = response.data.filter(student => 
            student.studentInfo?.parentIds && student.studentInfo.parentIds.length > 0
          );
          setLinkedStudents(studentsWithParents);
        } catch (error) {
          console.error('Error fetching linked students:', error);
          setLinkedStudents([]);
        }
      } else {
        setLinkedStudents([]);
      }
    };

    fetchUnlinkedStudents();
    fetchLinkedStudents();
  }, [formData.role, isEditMode]);

  // Fetch student parents and available parents when in edit mode for students
  useEffect(() => {
    const fetchStudentParents = async () => {
      if (formData.role === 'student' && isEditMode && id) {
        try {
          const response = await api.get(`/students/${id}/parents`);
          console.log('Student parents response:', response.data);
          setStudentParents(response.data.data || []);
        } catch (error) {
          console.error('Error fetching student parents:', error);
          setStudentParents([]);
        }
      } else {
        setStudentParents([]);
      }
    };

    const fetchAvailableParents = async () => {
      if (formData.role === 'student') {
        try {
          const response = await api.get('/users?role=parent');
          console.log('Available parents response:', response.data);
          setAvailableParents(response.data || []);
        } catch (error) {
          console.error('Error fetching available parents:', error);
          setAvailableParents([]);
        }
      } else {
        setAvailableParents([]);
      }
    };

    const fetchParentChildren = async () => {
      if (formData.role === 'parent' && isEditMode && id) {
        try {
          const response = await api.get(`/parents/children/${id}`);
          console.log('Parent children response:', response.data);
          setParentChildren(response.data.data || []);
        } catch (error) {
          console.error('Error fetching parent children:', error);
          setParentChildren([]);
        }
      } else {
        setParentChildren([]);
      }
    };

    fetchStudentParents();
    fetchAvailableParents();
    fetchParentChildren();
  }, [formData.role, isEditMode, id]);

  // Fetch user data when in edit mode
  useEffect(() => {
    if (isEditMode) {
      const fetchUser = async () => {
        try {
          setFetchingUser(true);
          const response = await api.get(`/users/${id}`);
          const userData = response.data.success ? response.data.data : response.data;

          console.log('Fetched user data:', userData);
          setUserData(userData); // Store for activation status section
          console.log('Student info:', userData.studentInfo);

          // Log specific fields for debugging assigned class
          if (userData.studentInfo) {
            console.log('Halaqa direct:', userData.studentInfo.halaqa);
            console.log('Halaqa ID:', userData.studentInfo.halaqa?._id);
            console.log('HalaqaId field:', userData.studentInfo.halaqaId);

            // Enhanced debug logging for halaqaId detection
            const possibleHalaqaValues = [
              userData.studentInfo.halaqa?._id,
              userData.studentInfo.halaqaId,
              userData.studentInfo.halaqa
            ];

            console.log('All possible halaqa values:', possibleHalaqaValues);
            console.log('Student info complete dump:', JSON.stringify(userData.studentInfo, null, 2));
          }

          if (userData) {
            // Split name into firstName and lastName if needed
            let firstName = '';
            let lastName = '';

            if (userData.name) {
              const nameParts = userData.name.split(' ');
              firstName = nameParts[0] || '';
              lastName = nameParts.slice(1).join(' ') || '';
            }

            // Set form data with user details
            setFormData({
              firstName,
              lastName,
              email: userData.email || '',
              phone: userData.phone || '',
              role: userData.role || '',
              center: userData.center?._id || userData.center || '',
              // Don't populate password fields for security, but mark them differently for editing mode
              password: '',
              confirmPassword: '',
              sendWelcomeEmail: false,
              // Added flag to track if we're in password update mode
              updatePassword: false,

              // Role-specific fields
              specializations: userData.teacherInfo?.specializations || '',
              // Convert age to dateOfBirth if available
              dateOfBirth: userData.studentInfo?.dateOfBirth || '',
              year: userData.studentInfo?.year || userData.studentInfo?.grade || '',
              // Extract assigned classes based on role
              assignedClasses: (() => {
                // For teachers
                if (userData.role === 'teacher') {
                  if (userData.halaqas && Array.isArray(userData.halaqas)) {
                    console.log('Found teacher halaqas:', userData.halaqas);
                    return userData.halaqas.map(id => id.toString());
                  }
                  if (userData.teacherInfo?.halaqas && Array.isArray(userData.teacherInfo.halaqas)) {
                    console.log('Found teacher halaqas in teacherInfo:', userData.teacherInfo.halaqas);
                    return userData.teacherInfo.halaqas.map(id => id.toString());
                  }
                  return [];
                }
                
                // For students
                if (userData.role === 'student' && userData.studentInfo) {
                  // Use halaqas array if available (multi-class students)
                  if (userData.studentInfo.halaqas && Array.isArray(userData.studentInfo.halaqas)) {
                    console.log('Found student halaqas array:', userData.studentInfo.halaqas);
                    return userData.studentInfo.halaqas.map(id => id.toString());
                  }

                  // Fallback to single halaqaId for backward compatibility
                  if (userData.studentInfo.halaqaId) {
                    console.log('Found single halaqaId, converting to array:', userData.studentInfo.halaqaId);
                    return [userData.studentInfo.halaqaId.toString()];
                  }

                  // Check for halaqa as object with _id (legacy)
                  if (userData.studentInfo.halaqa && typeof userData.studentInfo.halaqa === 'object' && userData.studentInfo.halaqa._id) {
                    console.log('Found halaqa as object with _id:', userData.studentInfo.halaqa._id);
                    return [userData.studentInfo.halaqa._id.toString()];
                  }

                  // Check for halaqa as string (legacy)
                  if (userData.studentInfo.halaqa && typeof userData.studentInfo.halaqa === 'string') {
                    console.log('Found halaqa as string:', userData.studentInfo.halaqa);
                    return [userData.studentInfo.halaqa.toString()];
                  }

                  console.log('No classes found in student info');
                  return [];
                }
                
                // Default for other roles
                return [];
              })(),
              
              // Parent-specific fields
              relationship: userData.parentInfo?.relationship || 'father',
              preferredContactMethod: userData.parentInfo?.preferredContactMethod || 'email',
              notes: userData.parentInfo?.notes || ''
            });
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        } finally {
          setFetchingUser(false);
        }
      };

      fetchUser();
    }
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle sendWelcomeEmail checkbox for teachers and parents
    if (name === 'sendWelcomeEmail' && type === 'checkbox' && (formData.role === 'teacher' || formData.role === 'parent')) {
      setFormData(prev => ({
        ...prev,
        [name]: checked,
        // Clear password fields when email activation is enabled
        password: checked ? '' : prev.password,
        confirmPassword: checked ? '' : prev.confirmPassword
      }));
      
      // Clear password-related errors when email activation is enabled
      if (checked) {
        setFormErrors(prev => ({ 
          ...prev, 
          password: '', 
          confirmPassword: '' 
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleMultiSelectChange = (e) => {
    const { name, options } = e.target;
    const selectedValues = Array.from(options)
      .filter(option => option.selected)
      .map(option => option.value);
    
    setFormData(prev => ({
      ...prev,
      [name]: selectedValues
    }));
  };

  // Handle parent management functions
  const handleAddParent = async (parentId) => {
    try {
      await api.post(`/parents/link-child/${parentId}/${id}`);
      // Refresh parent list
      const response = await api.get(`/students/${id}/parents`);
      setStudentParents(response.data.data || []);
      console.log('Parent added successfully');
    } catch (error) {
      console.error('Error adding parent:', error);
      alert('Failed to add parent. Please try again.');
    }
  };

  const handleRemoveParent = async (parentId) => {
    if (!window.confirm('Are you sure you want to remove this parent?')) {
      return;
    }
    
    try {
      await api.delete(`/parents/unlink-child/${parentId}/${id}`);
      // Refresh parent list
      const response = await api.get(`/students/${id}/parents`);
      setStudentParents(response.data.data || []);
      console.log('Parent removed successfully');
    } catch (error) {
      console.error('Error removing parent:', error);
      alert('Failed to remove parent. Please try again.');
    }
  };

  // Handle checkbox changes for multi-class selection
  const handleClassToggle = (classId) => {
    setFormData(prev => {
      const currentClasses = prev.assignedClasses || [];
      const isSelected = currentClasses.includes(classId);
      
      let newClasses;
      if (isSelected) {
        // Remove class if already selected
        newClasses = currentClasses.filter(id => id !== classId);
      } else {
        // Add class if not selected
        newClasses = [...currentClasses, classId];
      }
      
      console.log('Class selection changed:', { classId, isSelected, newClasses });
      
      return {
        ...prev,
        assignedClasses: newClasses
      };
    });
  };

  const validateForm = () => {
    const errors = {};

    // Validate required fields
    if (!formData.firstName.trim()) errors.firstName = 'First name is required';
    if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
    
    // Email is required for all roles except 'student'
    if (formData.role !== 'student') {
      if (!formData.email.trim()) errors.email = 'Email is required';
      if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Email is invalid';
    } else {
      // For students, validate email only if one is provided
      if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Email is invalid';
    }
    
    if (!formData.role) errors.role = 'Role is required';
    if (!formData.center) errors.center = 'Center is required';

    // Parent-specific validation
    if (formData.role === 'parent') {
      // Relationship is required for parents
      if (!formData.relationship || !formData.relationship.trim()) {
        errors.relationship = 'Please select your relationship to the child';
      } else {
        // Validate relationship is in allowed values
        const validRelationships = ['father', 'mother', 'guardian', 'grandparent', 'other'];
        if (!validRelationships.includes(formData.relationship)) {
          errors.relationship = 'Invalid relationship type';
        }
      }

      // Validate preferred contact method
      if (formData.preferredContactMethod) {
        const validContactMethods = ['email', 'phone', 'sms', 'app_notification'];
        if (!validContactMethods.includes(formData.preferredContactMethod)) {
          errors.preferredContactMethod = 'Invalid contact method';
        }
      }

      // Validate notes length
      if (formData.notes && formData.notes.length > 500) {
        errors.notes = 'Admin notes cannot exceed 500 characters';
      }
    }

    // Password validation - required for new users (except students) or when explicitly updating password
    if (!isEditMode || formData.updatePassword) {
      // For new users: password is required for all roles except students (optional for students)
      // Exception: Teachers/Parents with welcome email don't need password (they'll set it via email)
      const isUserWithEmail = (formData.role === 'teacher' || formData.role === 'parent') && formData.sendWelcomeEmail;
      if (!isEditMode && formData.role !== 'student' && !isUserWithEmail) {
        if (!formData.password) {
          errors.password = 'Password is required';
        } else if (formData.password.length < 6) {
          errors.password = 'Password must be at least 6 characters';
        }
      }
      // For students (new) or edit mode with updatePassword: validate only if password is provided
      else {
        if (formData.password && formData.password.length < 6) {
          errors.password = 'Password must be at least 6 characters';
        }
      }

      // Confirm password validation: only if password is provided
      if (formData.password && formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }
    
    // Role-specific validation
    if (formData.role === 'teacher') {
      // Teacher-specific validation if needed
    } else if (formData.role === 'student') {
      // Both dateOfBirth and year are optional now
      // No validation needed for these fields
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await api.delete(`/users/${id}`);
      setShowDeleteModal(false);
      navigate('/users');
    } catch (error) {
      console.error('Error deleting user:', error);
      setFormErrors(prev => ({
        ...prev,
        submit: error.response?.data?.message || 'Failed to delete user'
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleResendWelcomeEmail = async () => {
    if (!userData || !id) return;

    setResendingEmail(true);
    setFormErrors({}); // Clear any existing errors

    try {
      const response = await api.post(`/users/${id}/resend-welcome-email`);
      
      if (response.data.success) {
        setLastEmailSent(new Date().toLocaleString());
        // Update userData with new token expiry
        if (response.data.tokenExpires) {
          setUserData(prev => ({
            ...prev,
            resetTokenExpires: response.data.tokenExpires
          }));
        }
        
        // Show success message
        alert(`Welcome email sent successfully to ${userData.email}`);
      } else {
        throw new Error(response.data.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error resending welcome email:', error);
      setFormErrors({ 
        submit: error.response?.data?.message || 'Failed to resend welcome email' 
      });
    } finally {
      setResendingEmail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Prepare base user data
      let userData = {};

      // For a new user or a non-nested-field update, use the normal structure
      if (!isEditMode) {
        userData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          role: formData.role,
          center: formData.center
        };

        // Add sendWelcomeEmail flag only for new users
        userData.sendWelcomeEmail = formData.sendWelcomeEmail;

        // Add password for new users
        if (formData.password) {
          userData.password = formData.password;
        }

        // Add role-specific data for new users
        if (formData.role === 'teacher') {
          userData.teacherInfo = {
            specializations: formData.specializations,
            halaqas: formData.assignedClasses
          };
          // Also add halaqas at the top level for backend compatibility
          userData.halaqas = formData.assignedClasses;
        } else if (formData.role === 'student') {
          userData.studentInfo = {
            dateOfBirth: formData.dateOfBirth || '',
            year: formData.year || '',
          };

          // Add assigned classes if any are selected
          if (formData.assignedClasses && formData.assignedClasses.length > 0) {
            userData.studentInfo.halaqas = formData.assignedClasses;
            userData.studentInfo.halaqaId = formData.assignedClasses[0]; // Set first class as primary for backward compatibility
            console.log('Sending assigned classes:', formData.assignedClasses);
          }

          // Add selected parents for new student creation
          if (formData.selectedParents && formData.selectedParents.length > 0) {
            userData.selectedParents = formData.selectedParents;
            console.log('Sending selected parents:', formData.selectedParents);
          }

          console.log('Sending student info:', userData.studentInfo);
        } else if (formData.role === 'parent') {
          // Add parent-specific fields
          userData.parentInfo = {
            relationship: formData.relationship,
            preferredContactMethod: formData.preferredContactMethod,
            notes: formData.notes
          };
          
          // Add linked children for parents
          userData.linkedChildren = formData.linkedChildren;
          userData.additionalChildren = formData.additionalChildren;
          console.log('Sending parent info:', userData.parentInfo);
          console.log('Sending linked children:', userData.linkedChildren);
          console.log('Sending additional children:', userData.additionalChildren);
        }
      } else {
        // For editing an existing user, use a flat structure to work with findByIdAndUpdate
        userData = {
          // Basic user fields
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          email: formData.email,
          phone: formData.phone,
          role: formData.role,
          center: formData.center
        };

        // Add password only if updatePassword is checked
        if (formData.updatePassword && formData.password) {
          userData.password = formData.password;
        }

        // Handle teacher-specific fields in a flat structure
        if (formData.role === 'teacher') {
          userData.specializations = formData.specializations;
          userData.halaqas = formData.assignedClasses;
          console.log('Sending teacher halaqas:', formData.assignedClasses);
        }
        // Handle student-specific fields in a flat structure
        else if (formData.role === 'student') {
          // Using top-level fields for student info
          userData.dateOfBirth = formData.dateOfBirth || '';
          userData.year = formData.year || '';

          if (formData.assignedClasses && formData.assignedClasses.length > 0) {
            userData['studentInfo.halaqas'] = formData.assignedClasses;
            userData['studentInfo.halaqaId'] = formData.assignedClasses[0]; // Set first class as primary
            console.log('Sending assigned classes:', formData.assignedClasses);
          }
        }
      }

      let response;

      if (isEditMode) {
        // Update existing user
        response = await api.put(`/users/${id}`, userData);

        // For teacher role, we need to make additional API call to update the teacher record
        if (formData.role === 'teacher' && response.data) {
          try {
            // Check if teacher record exists
            const teacherResponse = await api.get(`/teachers?userId=${id}`);
            const teacherData = teacherResponse.data.success ? teacherResponse.data.data : teacherResponse.data;

            // If teacher record exists, update it
            if (Array.isArray(teacherData) && teacherData.length > 0) {
              const teacherId = teacherData[0]._id;

              // Update teacher record with halaqas and specializations
              await api.put(`/teachers/${teacherId}`, {
                halaqas: formData.assignedClasses,
                specializations: formData.specializations,
                userId: id,
                centerId: formData.center
              });

              // Also update the user record with halaqas directly
              await api.put(`/users/${id}`, {
                name: `${formData.firstName} ${formData.lastName}`.trim(),
                halaqas: formData.assignedClasses
              });

              console.log('Updated teacher record separately and user.halaqas field');
            }
          } catch (teacherError) {
            console.error('Error updating teacher record:', teacherError);
            // Continue even if teacher record update fails
          }
        }

        // For student role, we need to send a direct API call to update studentInfo
        else if (formData.role === 'student' && response.data) {
          try {
            // First, get current student data to preserve existing parentIds
            const currentStudentResponse = await api.get(`/users/${id}`);
            const currentStudent = currentStudentResponse.data.success ? currentStudentResponse.data.data : currentStudentResponse.data;
            const existingParentIds = currentStudent?.studentInfo?.parentIds || [];
            
            console.log('Preserving existing parentIds during student update:', existingParentIds);

            // Make a direct API call to update the user again with student-specific info
            const studentData = {
              studentInfo: {
                dateOfBirth: formData.dateOfBirth || '',
                year: formData.year || '',
                // CRITICAL: Preserve existing parentIds to prevent overwriting parent relationships
                parentIds: existingParentIds
              }
            };

            // Add assigned classes if provided
            if (formData.assignedClasses && formData.assignedClasses.length > 0) {
              studentData.studentInfo.halaqas = formData.assignedClasses;
              studentData.studentInfo.halaqaId = formData.assignedClasses[0]; // Set first class as primary
              console.log('Adding classes to direct student update:', formData.assignedClasses);
            }

            // Alternative approach: Update user's name to trigger full save operation
            // This will force mongoose to save the document properly
            const studentUpdateTrigger = {
              name: `${formData.firstName} ${formData.lastName}`.trim(),
              studentInfo: studentData.studentInfo
            };

            // Re-send the update using the regular user update endpoint
            await api.put(`/users/${id}`, studentUpdateTrigger);
            console.log('Sent student update by updating name to trigger save (with preserved parentIds)');
          } catch (studentError) {
            console.error('Error with direct student update:', studentError);
            // Continue even if student info update fails
          }
        }
      } else {
        // Create new user
        response = await api.post('/users', userData);
      }

      if (response.data) {
        navigate('/users');
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} user:`, error);
      setFormErrors(prev => ({
        ...prev,
        submit: error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} user`
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-user-container">
      <div className="header">
        <h1>{isEditMode ? 'Edit User' : 'Create New User'}</h1>
        <Link to="/users" className="back-btn">
          Back to Users
        </Link>
      </div>

      <div className="form-card">
        {fetchingUser ? (
          <div className="loading-indicator">
            <div className="spinner"></div>
            <p>Loading user data...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {formErrors.submit && (
              <div className="error-message">{formErrors.submit}</div>
            )}
          
          <div className="form-section">
            <h2>Basic Information</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={formErrors.firstName ? 'error' : ''}
                />
                {formErrors.firstName && <div className="error-text">{formErrors.firstName}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={formErrors.lastName ? 'error' : ''}
                />
                {formErrors.lastName && <div className="error-text">{formErrors.lastName}</div>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">
                  Email {formData.role !== 'student' ? '*' : '(optional for students)'}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={formErrors.email ? 'error' : ''}
                  placeholder={formData.role === 'student' ? 'Optional for students' : 'Enter email'}
                />
                {formErrors.email && <div className="error-text">{formErrors.email}</div>}
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="text"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="role">Role *</label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className={formErrors.role ? 'error' : ''}
                >
                  <option value="">Select a role</option>
                  <option value="admin">Admin</option>
                  <option value="teacher">Teacher</option>
                  <option value="student">Student</option>
                  <option value="parent">Parent</option>
                </select>
                {formErrors.role && <div className="error-text">{formErrors.role}</div>}
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="center">Center *</label>
                <select
                  id="center"
                  name="center"
                  value={formData.center}
                  onChange={handleChange}
                  className={formErrors.center ? 'error' : ''}
                >
                  <option value="">Select a center</option>
                  {centers.map(center => (
                    <option key={center._id} value={center._id}>
                      {center.name}
                    </option>
                  ))}
                </select>
                {formErrors.center && <div className="error-text">{formErrors.center}</div>}
              </div>
            </div>
          </div>
          
          {/* Role-specific fields */}
          {formData.role === 'teacher' && (
            <div className="form-section">
              <h2>Teacher Information</h2>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="specializations">Specializations</label>
                  <input
                    type="text"
                    id="specializations"
                    name="specializations"
                    value={formData.specializations}
                    onChange={handleChange}
                    placeholder="E.g., Tajweed, Hifz, Tafsir"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="assignedClasses">Assigned Classes</label>
                  <select
                    id="assignedClasses"
                    name="assignedClasses"
                    multiple
                    value={formData.assignedClasses}
                    onChange={handleMultiSelectChange}
                    className="multi-select"
                  >
                    {classes.map(classItem => (
                      <option key={classItem._id} value={classItem._id}>
                        {classItem.name}
                      </option>
                    ))}
                  </select>
                  <small className="helper-text">Hold Ctrl (or Cmd) to select multiple classes</small>
                </div>
              </div>
            </div>
          )}
          
          {formData.role === 'student' && (
            <div className="form-section">
              <h2>Student Information</h2>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dateOfBirth">Date of Birth</label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    placeholder="DD/MM/YYYY"
                  />
                  <small className="helper-text">Optional (DD/MM/YYYY format)</small>
                </div>

                <div className="form-group">
                  <label htmlFor="year">Year</label>
                  <input
                    type="text"
                    id="year"
                    name="year"
                    value={formData.year}
                    onChange={handleChange}
                    placeholder="E.g., Year 10, KS3"
                  />
                  <small className="helper-text">Optional</small>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="assignedClasses">Assigned Classes</label>
                  <div className="checkbox-list">
                    {classes.map(classItem => {
                      const isSelected = formData.assignedClasses.includes(classItem._id);
                      return (
                        <div key={classItem._id} className="checkbox-group">
                          <input
                            type="checkbox"
                            id={`class-${classItem._id}`}
                            checked={isSelected}
                            onChange={() => handleClassToggle(classItem._id)}
                          />
                          <label htmlFor={`class-${classItem._id}`}>
                            {classItem.name}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                  <small className="helper-text">Select one or more classes for this student</small>
                </div>
              </div>

              {/* Parent Selection for New Students */}
              {!isEditMode && availableParents.length > 0 && (
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="selectedParents">Link to Parents (Optional)</label>
                    <div className="checkbox-list">
                      {availableParents.map(parent => {
                        const isSelected = formData.selectedParents.includes(parent._id);
                        return (
                          <div key={parent._id} className="checkbox-group">
                            <input
                              type="checkbox"
                              id={`parent-${parent._id}`}
                              checked={isSelected}
                              onChange={(e) => {
                                const updatedParents = e.target.checked
                                  ? [...formData.selectedParents, parent._id]
                                  : formData.selectedParents.filter(id => id !== parent._id);
                                
                                setFormData(prev => ({
                                  ...prev,
                                  selectedParents: updatedParents
                                }));
                              }}
                            />
                            <label htmlFor={`parent-${parent._id}`}>
                              <strong>{parent.name}</strong>
                              {parent.email && <span> - {parent.email}</span>}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                    <small className="helper-text">
                      Select parents to link to this student during creation
                    </small>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Parent Information Section for Students in Edit Mode */}
          {formData.role === 'student' && isEditMode && (
            <div className="form-section">
              <h2>Parent/Guardian Information</h2>
              
              {/* Current Parents */}
              {studentParents.length > 0 ? (
                <div className="form-group">
                  <label>Current Parents ({studentParents.length})</label>
                  <div className="parent-list">
                    {studentParents.map(parent => (
                      <div key={parent._id} className="parent-card">
                        <div className="parent-info">
                          <div className="parent-name">
                            <strong>{parent.name}</strong>
                          </div>
                          <div className="parent-contact">
                            {parent.email && <span>Email: {parent.email}</span>}
                            {parent.phone && <span>Phone: {parent.phone}</span>}
                          </div>
                        </div>
                        <div className="parent-actions">
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => handleRemoveParent(parent._id)}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="form-group">
                  <p className="no-data-message">No parents linked to this student</p>
                </div>
              )}
              
              {/* Add New Parent */}
              {availableParents.length > 0 && (
                <div className="form-group">
                  <label>Add Parent</label>
                  <div className="add-parent-section">
                    {availableParents
                      .filter(parent => !studentParents.some(sp => sp._id === parent._id))
                      .map(parent => (
                        <div key={parent._id} className="available-parent-item">
                          <span className="parent-details">
                            <strong>{parent.name}</strong>
                            {parent.email && <span> - {parent.email}</span>}
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => handleAddParent(parent._id)}
                          >
                            Add
                          </button>
                        </div>
                      ))
                    }
                  </div>
                  <small className="helper-text">
                    Select parents to link to this student
                  </small>
                </div>
              )}
            </div>
          )}
          
          {formData.role === 'parent' && (
            <div className="form-section">
              <h2>Parent Information</h2>
              
              <div className="form-group">
                <label htmlFor="relationship">Relationship to Child</label>
                <select
                  id="relationship"
                  name="relationship"
                  value={formData.relationship}
                  onChange={handleChange}
                  className={formErrors.relationship ? 'form-control error' : 'form-control'}
                >
                  <option value="">Select relationship...</option>
                  <option value="father">Father</option>
                  <option value="mother">Mother</option>
                  <option value="guardian">Guardian</option>
                  <option value="grandparent">Grandparent</option>
                  <option value="other">Other</option>
                </select>
                {formErrors.relationship && <div className="error-text">{formErrors.relationship}</div>}
              </div>
              
              <div className="form-group">
                <label htmlFor="preferredContactMethod">Preferred Contact Method</label>
                <select
                  id="preferredContactMethod"
                  name="preferredContactMethod"
                  value={formData.preferredContactMethod}
                  onChange={handleChange}
                  className={formErrors.preferredContactMethod ? 'form-control error' : 'form-control'}
                >
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="sms">SMS</option>
                  <option value="app_notification">App Notification</option>
                </select>
                {formErrors.preferredContactMethod && <div className="error-text">{formErrors.preferredContactMethod}</div>}
              </div>
              
              
              {(user?.role === 'admin' || user?.role === 'super_admin') && (
                <div className="form-group">
                  <label htmlFor="notes">Admin Notes</label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    className={formErrors.notes ? 'form-control error' : 'form-control'}
                    placeholder="Internal notes about this parent..."
                    rows={3}
                  />
                  {formErrors.notes && <div className="error-text">{formErrors.notes}</div>}
                </div>
              )}
            </div>
          )}
          
          {formData.role === 'parent' && !isEditMode && (
            <div className="form-section">
              <h2>Linked Children (Optional)</h2>
              
              <div className="form-group">
                <label htmlFor="linkedChildren">Select Students to Link</label>
                {unlinkedStudents.length > 0 ? (
                  <div className="checkbox-list">
                    {unlinkedStudents.map(student => {
                      const isSelected = formData.linkedChildren.includes(student._id);
                      // Get class name from populated data
                      const className = student.studentInfo?.halaqas?.[0]?.name || 
                                       student.studentInfo?.halaqaId?.name || 
                                       'No Class';
                      
                      return (
                        <div key={student._id} className="checkbox-group">
                          <input
                            type="checkbox"
                            id={`child_${student._id}`}
                            checked={isSelected}
                            onChange={(e) => {
                              const updatedChildren = e.target.checked
                                ? [...formData.linkedChildren, student._id]
                                : formData.linkedChildren.filter(id => id !== student._id);
                              
                              setFormData(prev => ({
                                ...prev,
                                linkedChildren: updatedChildren
                              }));
                            }}
                          />
                          <label htmlFor={`child_${student._id}`}>
                            {student.name} - {className}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="no-data-message">
                    No unlinked students available. All students are already linked to parents or you can create students first.
                  </p>
                )}
                <small className="helper-text">
                  Select students to link as children to this parent account
                </small>
              </div>

              {/* Section for adding as second/additional parent to already linked students */}
              {linkedStudents.length > 0 && (
                <div className="form-group" style={{ marginTop: '2rem', borderTop: '1px solid #e0e0e0', paddingTop: '1rem' }}>
                  <label htmlFor="additionalChildren">Add as Additional Parent (Second Parent)</label>
                  <div className="checkbox-list">
                    {linkedStudents.map(student => {
                      const isSelected = formData.additionalChildren.includes(student._id);
                      // Get class name from populated data
                      const className = student.studentInfo?.halaqas?.[0]?.name || 
                                       student.studentInfo?.halaqaId?.name || 
                                       'No Class';
                      
                      return (
                        <div key={student._id} className="checkbox-group">
                          <input
                            type="checkbox"
                            id={`additional_child_${student._id}`}
                            checked={isSelected}
                            onChange={(e) => {
                              const updatedChildren = e.target.checked
                                ? [...formData.additionalChildren, student._id]
                                : formData.additionalChildren.filter(id => id !== student._id);
                              
                              setFormData(prev => ({
                                ...prev,
                                additionalChildren: updatedChildren
                              }));
                            }}
                          />
                          <label htmlFor={`additional_child_${student._id}`}>
                            {student.name} - {className} (has {student.studentInfo.parentIds.length} parent{student.studentInfo.parentIds.length > 1 ? 's' : ''})
                          </label>
                        </div>
                      );
                    })}
                  </div>
                  <small className="helper-text">
                    Select students who already have parents to add this parent as an additional guardian
                  </small>
                </div>
              )}
            </div>
          )}

          {/* Parent Information Section for Parents in Edit Mode */}
          {formData.role === 'parent' && isEditMode && (
            <div className="form-section">
              <h2>Linked Students</h2>
              
              {/* Current Linked Students */}
              {parentChildren.length > 0 ? (
                <div className="form-group">
                  <label>Currently Linked Students ({parentChildren.length})</label>
                  <div className="linked-students-list">
                    {parentChildren.map(student => (
                      <div key={student._id} className="student-card" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '6px',
                        marginBottom: '8px',
                        backgroundColor: '#f8f9fa'
                      }}>
                        <div className="student-info">
                          <strong>{student.name}</strong>
                          {student.studentInfo?.halaqas?.[0]?.name && (
                            <span style={{ marginLeft: '8px', color: '#666', fontSize: '14px' }}>
                              - Class: {student.studentInfo.halaqas[0].name}
                            </span>
                          )}
                          {student.hasMultipleParents && (
                            <span style={{
                              marginLeft: '8px',
                              fontSize: '12px',
                              color: '#666',
                              backgroundColor: '#e8f4fd',
                              padding: '2px 6px',
                              borderRadius: '10px'
                            }}>
                              👨‍👩‍👧‍👦 Multiple parents
                            </span>
                          )}
                        </div>
                        <div className="student-actions" style={{ fontSize: '14px', color: '#666' }}>
                          <span>📚 Linked Student</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffeaa7',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}>
                    <strong>💡 Note:</strong> To manage parent-student relationships (add/remove), please use the{' '}
                    <strong>Student Management screen</strong> → Edit Student → Parent Information section.
                  </div>
                </div>
              ) : (
                <div className="form-group">
                  <div style={{
                    padding: '20px',
                    textAlign: 'center',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e0e0e0',
                    borderRadius: '6px',
                    color: '#666'
                  }}>
                    <p><strong>No students linked to this parent.</strong></p>
                    <p style={{ marginTop: '8px', fontSize: '14px' }}>
                      To link students to this parent, go to <strong>Student Management</strong> → 
                      Edit Student → Parent Information section.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Account Activation Status Section */}
          {isEditMode && (formData.role === 'parent' || formData.role === 'teacher') && userData?.mustSetPassword && (
            <div className="form-section activation-section">
              <h2>Account Activation Status</h2>
              <div className="activation-status">
                {userData.resetTokenExpires && new Date(userData.resetTokenExpires) < new Date() ? (
                  <div className="status-warning">
                    <span className="status-icon">⚠️</span>
                    <span>Setup email expired on {new Date(userData.resetTokenExpires).toLocaleDateString()}</span>
                  </div>
                ) : userData.resetToken ? (
                  <div className="status-info">
                    <span className="status-icon">📧</span>
                    <span>Account setup email sent - expires {userData.resetTokenExpires ? new Date(userData.resetTokenExpires).toLocaleDateString() : 'Unknown'}</span>
                  </div>
                ) : (
                  <div className="status-error">
                    <span className="status-icon">❌</span>
                    <span>No setup email sent - account cannot be activated</span>
                  </div>
                )}
                
                <div className="activation-actions">
                  <button 
                    type="button" 
                    className="resend-email-btn"
                    onClick={handleResendWelcomeEmail}
                    disabled={resendingEmail}
                  >
                    {resendingEmail ? '🔄 Sending...' : '🔄 Resend Welcome Email'}
                  </button>
                  {lastEmailSent && (
                    <span className="last-sent">Last sent: {lastEmailSent}</span>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="form-section">
            <h2>Account Settings</h2>

            {isEditMode && (
              <div className="form-row">
                <div className="form-group checkbox-group">
                  <input
                    type="checkbox"
                    id="updatePassword"
                    name="updatePassword"
                    checked={formData.updatePassword}
                    onChange={handleChange}
                  />
                  <label htmlFor="updatePassword">
                    Update Password
                  </label>
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="password">
                  {isEditMode 
                    ? (formData.updatePassword ? 'New Password *' : 'Password') 
                    : (() => {
                        const isUserWithEmail = (formData.role === 'teacher' || formData.role === 'parent') && formData.sendWelcomeEmail;
                        if (formData.role === 'student') return 'Password (optional)';
                        if (isUserWithEmail) return 'Password (will be set via email)';
                        return 'Password *';
                      })()
                  }
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={formErrors.password ? 'error' : ''}
                  disabled={(isEditMode && !formData.updatePassword) || ((formData.role === 'teacher' || formData.role === 'parent') && formData.sendWelcomeEmail)}
                  placeholder={(formData.role === 'teacher' || formData.role === 'parent') && formData.sendWelcomeEmail ? 'Will be set via email setup' : ''}
                  autoComplete="new-password"
                />
                {formErrors.password && <div className="error-text">{formErrors.password}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">
                  {isEditMode 
                    ? (formData.updatePassword ? 'Confirm New Password *' : 'Confirm Password') 
                    : (() => {
                        const isUserWithEmail = (formData.role === 'teacher' || formData.role === 'parent') && formData.sendWelcomeEmail;
                        if (formData.role === 'student') return 'Confirm Password (optional)';
                        if (isUserWithEmail) return 'Confirm Password (not needed)';
                        return 'Confirm Password *';
                      })()
                  }
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={formErrors.confirmPassword ? 'error' : ''}
                  disabled={(isEditMode && !formData.updatePassword) || ((formData.role === 'teacher' || formData.role === 'parent') && formData.sendWelcomeEmail)}
                  placeholder={(formData.role === 'teacher' || formData.role === 'parent') && formData.sendWelcomeEmail ? 'Will be set via email setup' : ''}
                  autoComplete="new-password"
                />
                {formErrors.confirmPassword && (
                  <div className="error-text">{formErrors.confirmPassword}</div>
                )}
              </div>
            </div>

            {!isEditMode && (
              <div className="form-row">
                <div className="form-group checkbox-group">
                  <input
                    type="checkbox"
                    id="sendWelcomeEmail"
                    name="sendWelcomeEmail"
                    checked={formData.sendWelcomeEmail}
                    onChange={handleChange}
                  />
                  <label htmlFor="sendWelcomeEmail">
                    {(formData.role === 'teacher' || formData.role === 'parent')
                      ? 'Send welcome email with account setup instructions'
                      : 'Send welcome email with login credentials'
                    }
                  </label>
                </div>
              </div>
            )}
          </div>
          
          <div className="form-actions">
            <Link to="/users" className="cancel-btn">
              Cancel
            </Link>
            {isEditMode && (
              <button
                type="button"
                className="delete-btn"
                onClick={() => setShowDeleteModal(true)}
                disabled={loading || fetchingUser}
              >
                Delete User
              </button>
            )}
            <button
              type="submit"
              className="submit-btn"
              disabled={loading || fetchingUser}
            >
              {loading
                ? (isEditMode ? 'Saving...' : 'Creating...')
                : (isEditMode ? 'Save Changes' : 'Create User')}
            </button>
          </div>
        </form>
        )}
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <h3>Are you sure you want to delete this user?</h3>
            <p>This action cannot be undone. All data associated with this user will be permanently deleted.</p>
            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button 
                className="confirm-delete-btn"
                onClick={handleDelete}
              >
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateUserPage;